import {
  MultiFilterOperator,
  SearchZapsOrder,
  SearchZapsStatusWithAll,
} from "@zapier/bff";
import {
  isUntitledZap,
  normalizeZapNodeId,
  UNTITLED_ZAP_TITLE,
  ZAP_PAGE_SIZE,
} from "@zapier/utils";
import { useHydrateStorageZaps } from "./useHydrateStorageZaps";
import { SearchZap, useQuerySearchZaps } from "./useQuerySearchZaps";
import { StorageZap, useQueryStorageZaps } from "./useQueryStorageZaps";
import { useMemo } from "react";

interface Input {
  enabled?: boolean;
  lastRun?: string;
  order?: SearchZapsOrder;
  owner?: string;
  owners?: string;
  ownersOperator?: MultiFilterOperator;
  appId?: string;
  apps?: string;
  appsOperator?: MultiFilterOperator;
  page?: number | null;
  pageSize?: number;
  search?: string;
  status?: SearchZapsStatusWithAll;
  connection?: number;
  connections?: string;
  limit?: number;
}

// Search rows are actionable before storage metadata arrives.
export type Zap = Omit<SearchZap, keyof StorageZap> &
  Partial<StorageZap> &
  Pick<StorageZap, "id" | "customuserId"> &
  Pick<SearchZap, "title" | "updatedAt">;

interface Return {
  isLoading: boolean;
  isFetching: boolean;
  totalPages: number;
  totalCount: number;
  zaps: Zap[];
}

export function useSearchZaps({
  enabled = true,
  lastRun,
  order = "-updated_at",
  owner = "",
  owners,
  ownersOperator,
  appId = "",
  apps,
  appsOperator,
  connection,
  connections,
  page = 1,
  pageSize = ZAP_PAGE_SIZE,
  search = "",
  status = "all",
  limit,
}: Input): Return {
  const { response, isLoading, isFetching } = useQuerySearchZaps({
    enabled,
    appId,
    apps,
    appsOperator,
    connection,
    connections,
    lastRun,
    page,
    pageSize,
    search,
    order,
    owner,
    owners,
    ownersOperator,
    status,
    limit,
  });
  const searchZaps = useMemo(() => {
    return response?.results || ([] as SearchZap[]);
  }, [response?.results]);

  const zapIds = searchZaps.map((zap: SearchZap) => zap.id);

  // Fetch Storage Zaps from the Storage API
  const { response: storageZaps = {} } = useQueryStorageZaps({ ids: zapIds });
  const storageZapList = useMemo(() => {
    const seen = new Set<string>();

    return Object.values(storageZaps).filter((zap) => {
      if (!zap?.id || seen.has(zap.id)) {
        return false;
      }

      seen.add(zap.id);
      return true;
    });
  }, [storageZaps]);
  const { list: hydratedZaps, hash: hydratedZapsHash } =
    useHydrateStorageZaps(storageZapList);

  // Map Zaps to hydrate all data
  const zaps = useMemo(() => {
    return searchZaps.map((zap: SearchZap) => {
      const hydratedZap: StorageZap | undefined =
        hydratedZapsHash[normalizeZapNodeId(zap.zapId)];

      if (!hydratedZap && hydratedZaps.length > 0) {
        console.error("Could not find storage zap:", zap.id, zap.title);
      }

      return {
        ...zap,
        searchZap: zap,
        ...hydratedZap,
        id: zap.zapId,
        customuserId: hydratedZap?.customuserId ?? zap.ownerId,
        updatedAt: hydratedZap?.updatedAt ?? zap.updatedAt,
        title: isUntitledZap(hydratedZap?.title)
          ? zap.title || UNTITLED_ZAP_TITLE
          : hydratedZap?.title || zap.title || UNTITLED_ZAP_TITLE,
      };
    });
  }, [searchZaps, hydratedZaps, hydratedZapsHash]);

  const totalPages = response?.count ? Math.ceil(response.count / pageSize) : 1;

  return {
    totalPages,
    totalCount: response?.count || 0,
    zaps,
    isLoading,
    isFetching,
  };
}
